import Box from 'components/shared/Box'
import { useApp } from 'store/steps'
import { FormattedMessage } from 'react-intl'
import { useCart } from 'store/cart-slice/hooks'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import Link from 'components/shared/Link'
import CalendarEarlyPurchaseInfo from './components/CalendarEarlyPurchaseInfo'
import CalendarLegendItem from './components/CalendarLegendItem'
import { EventPresenter } from 'store/api-slice/hooks'
import CalendarMaintenanceInfo from './components/CalendarMaintenanceInfo'
import { isCurrentDateWithinRange } from 'helpers/dates'

interface CalendarModalFooterProps {
    isHolidaysLegendVisible?: boolean
    isEventsLegendVisible?: boolean
    openHoursModal?: () => void
    eventsData?: EventPresenter[]
    testIdPrefix?: string
}

const CalendarModalLegend = ({
    isHolidaysLegendVisible,
    isEventsLegendVisible,
    openHoursModal,
    eventsData,
    testIdPrefix,
}: CalendarModalFooterProps) => {
    const { isCreatorAccommodation } = useApp()
    const { isStepEmpty } = useCart()
    const { isDarkThemeForTickets } = usePromotions()

    return (
        <Box isFullWidth>
            <Box gap={[16, 12]} isFullWidth>
                {isCreatorAccommodation && !isStepEmpty('accommodation') && (
                    <CalendarLegendItem
                        badge="highlight"
                        description={
                            <FormattedMessage
                                id="global.calendar.legend_village_staying_days"
                                defaultMessage="Dni Twojego pobytu w Suntago Village"
                            />
                        }
                        hasDarkMode={isDarkThemeForTickets}
                    />
                )}
                {!!isHolidaysLegendVisible && (
                    <>
                        <CalendarLegendItem
                            badge="icon"
                            badgeIcon="starYellow"
                            description={
                                <FormattedMessage
                                    id="global.modal.calendar_legend_holidays_prices"
                                    defaultMessage="Dni, w których obowiązują ceny wakacyjne, weekendowe lub świąteczne."
                                />
                            }
                            hasDarkMode={isDarkThemeForTickets}
                            isDescriptionDark
                        />
                        <CalendarLegendItem
                            badge="icon"
                            badgeIcon="starBlue"
                            hasDarkMode={isDarkThemeForTickets}
                            description={
                                <>
                                    <FormattedMessage
                                        id="global.modal.calendar_legend_holidays_opening_hours"
                                        defaultMessage="Gwiazdką oznaczyliśmy dni świąteczne, w których godziny otwarcia mogą się różnić."
                                    />{' '}
                                    <Link
                                        textVariant="bodyExtraSmall"
                                        variant={
                                            isDarkThemeForTickets
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                        icon={null}
                                        onClick={openHoursModal}
                                        data-testid={
                                            testIdPrefix
                                                ? `${testIdPrefix}-calendar-button-opening-hours`
                                                : undefined
                                        }
                                    >
                                        <FormattedMessage
                                            id="global.btn.check_opening_hours"
                                            defaultMessage="Sprawdź godziny otwarcia"
                                        />
                                    </Link>
                                </>
                            }
                            isDescriptionDark
                        />
                    </>
                )}
                {!!isEventsLegendVisible && eventsData && (
                    <Box gap={[16, 12]}>
                        {eventsData?.map((event, index) => {
                            const { iconColor, legendDescription } = event

                            return iconColor && legendDescription ? (
                                <CalendarLegendItem
                                    key={`event-legend-${index}`}
                                    badge="color"
                                    badgeColor={iconColor}
                                    description={legendDescription}
                                    hasDarkMode={isDarkThemeForTickets}
                                />
                            ) : null
                        })}
                    </Box>
                )}
            </Box>

            <Box mt={24} gap={8} isFullWidth>
                <CalendarEarlyPurchaseInfo
                    hasDarkMode={isDarkThemeForTickets}
                />
                {isCurrentDateWithinRange('2024-09-30', '2024-11-21') && (
                    <CalendarMaintenanceInfo
                        hasDarkMode={isDarkThemeForTickets}
                    />
                )}
            </Box>
        </Box>
    )
}

export default CalendarModalLegend
